<template>
  <v-data-table
    class="batch-table"
    data-test="batch-table"
    :header-props="{ sortIcon: 'mdi-menu-up' }"
    :loading="isFetching"
    :headers="headers"
    :items="batches"
    :items-per-page="itemsPerPage"
    mobile-breakpoint="0"
    :disable-sort="true"
    fixed-header
    hide-default-footer
    :item-class="({ priority, status }) => ['table-row', priority.name, status]"
    loading-text="Loading..."
  >
    <template #no-data>
      <div class="d-flex align-center">
        <vs-text type="subdued">No batches found.</vs-text>
      </div>
    </template>

    <template #progress>
      <v-progress-linear color="blue" absolute indeterminate />
    </template>

    <template #item="{ item }">
      <tr
        :key="item.id"
        :data-test="`batch-table-row(${item.number})`"
        @click="handleRowClicked(item)"
      >
        <template v-for="column in headers">
          <batch-column
            :key="`${item.id}__${column.value}`"
            :value="column.value"
            :formatter="column.formatter"
            :component="column.component"
            :batch="item"
          />
        </template>
      </tr>
    </template>
  </v-data-table>
</template>

<script setup lang="ts">
import { useOrderedBatchColumns } from '@/components/batches/useOrderedBatchColumn'
import VsText from '@/components/vision/VsText.vue'
import { BatchSearchDto } from '@/models/batch'
import { useBatchSideSheetNavigation } from '@/components/jobs/navigation'
import BatchColumn from '@/components/batches/BatchColumn'

const props = defineProps<{
  isFetching: boolean
  batches: BatchSearchDto[]
  itemsPerPage: number
}>()

const { headers } = useOrderedBatchColumns()

const { handleRowClicked } = useBatchSideSheetNavigation('batch-search', 'batch-info-panel')
</script>

<style scoped>
.batch-table {
  flex: 1;
  overflow: hidden;
}

.batch-table-row-selected {
  background-color: var(--color-grey--lightest);
}

.batch-table-row-bulk-selected {
  background-color: var(--color-blue--lightest);
}

.batch-table-row-selected:hover {
  background-color: var(--color-blue--lighter) !important;
}

.batch-table >>> .v-data-table__wrapper {
  height: 100%;
}

.batch-table >>> .v-data-table__progress {
  margin: 0;
  padding: 0 !important;
}

.batch-table >>> .v-data-table__progress .v-progress-linear {
  margin: 0;
}

.batch-table >>> th[role='columnheader'] {
  white-space: nowrap;
  height: auto !important;
  z-index: var(--elevation-base);
  padding: var(--space-small) var(--space-large) !important;
}

.batch-table >>> table tbody tr {
  vertical-align: top;
}

.batch-table.archived >>> table tbody tr {
  opacity: 0.6;
}

.batch-table >>> table tbody tr td {
  padding: var(--space-base) var(--space-large) !important;
  white-space: nowrap;
  height: auto;
}
</style>
